/**
 * Sprint 26.0 — "Instructions for the agent" (stored agent-facing field).
 *
 * A distinct field from the task Body, marked agent-facing with a 2px purple LEFT
 * ACCENT implemented as a separate inner element (NOT a single-sided border —
 * design-rules §4: that would clip the card's radius). Carries the verbatim
 * "stored now · injected from 25.5" marker — this sprint STORES + renders it but
 * does NOT inject it into the agent prompt (the `instructions-stored-not-
 * injected` contract test guards that boundary; it inverts in Sprint 25.5).
 */

import { useState, type CSSProperties } from "react";

// D-L6 (№22.5 ride-along) — "the agent prompt" → "the hire's prompt". Copy only; the
// contract it describes is unchanged and nothing asserts this string.
const MARKER_TEXT = "Stored now · injected into the hire's prompt from sprint 25.5";

export interface InstructionsSectionProps {
  value: string;
  onSave: (text: string) => void;
}

export function InstructionsSection({ value, onSave }: InstructionsSectionProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  function commit() {
    setEditing(false);
    if (draft !== value) onSave(draft);
  }

  return (
    <section style={{ marginBottom: 16 }} data-testid="instructions-section">
      <div style={headerRow}>
        <span style={label}>Instructions for the hire</span>
        {!editing && (
          <button type="button" style={editBtn} onClick={() => { setDraft(value); setEditing(true); }}>
            <i className="ti ti-edit" style={{ fontSize: 12 }} aria-hidden="true" /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <div style={cardWrap}>
          <span style={accent} aria-hidden="true" />
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            style={textarea}
            data-testid="instructions-textarea"
          />
        </div>
      ) : (
        <div style={cardWrap}>
          <span style={accent} aria-hidden="true" />
          {value.trim() ? (
            <div style={text}>{value}</div>
          ) : (
            <div style={empty}>Optional — directive guidance for the hire.</div>
          )}
        </div>
      )}
      <div style={marker} data-testid="instructions-marker">
        <i className="ti ti-info-circle" style={{ fontSize: 12 }} aria-hidden="true" />
        {MARKER_TEXT}
      </div>
    </section>
  );
}

const headerRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 6,
};

const label: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
};

const editBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
};

const cardWrap: CSSProperties = {
  position: "relative",
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "10px 12px 10px 16px",
  overflow: "hidden",
};

const accent: CSSProperties = {
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  width: 2,
  background: "var(--c-purple-400)",
};

const text: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.6,
  color: "var(--color-text-primary)",
  whiteSpace: "pre-wrap",
};

const empty: CSSProperties = {
  fontSize: 13,
  fontStyle: "italic",
  color: "var(--color-text-tertiary)",
};

const textarea: CSSProperties = {
  width: "100%",
  minHeight: 70,
  background: "transparent",
  border: "none",
  padding: 0,
  fontSize: 13,
  lineHeight: 1.6,
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  resize: "vertical",
  outline: "none",
  boxSizing: "border-box",
};

const marker: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  marginTop: 8,
  padding: "3px 8px",
  borderRadius: 999,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-tertiary)",
  fontSize: 11,
};
